/**
 * Canonical permission keys for RAM-OS.
 *
 * Format: `resource.action` (lowercase, dot-separated, no nested dots).
 *
 * These keys are the single source of truth used by:
 *   - the engine's `@RequirePermission(...)` guard,
 *   - the helm's permission-aware UI (Settings → Roles & Permissions),
 *   - the seed data for the default role taxonomy (Admin / Operations / Documentation / External Agent).
 *
 * Adding a new key requires:
 *   1. Add the constant here.
 *   2. Add it to a role in the default seed.
 *   3. Document the resource + action in the Notion Decisions log if it's a v1 capability.
 */

// Shipments
export const SHIPMENTS_READ = "shipments.read" as const
export const SHIPMENTS_WRITE = "shipments.write" as const
export const SHIPMENTS_EXPORT = "shipments.export" as const // F1 — XLSX export

// Documents
export const DOCUMENTS_READ = "documents.read" as const
export const DOCUMENTS_WRITE = "documents.write" as const
export const DOCUMENTS_AUTOFLAG_CONFIGURE = "documents.autoflag.configure" as const // F2 L2 — per-Document-type auto-flag toggle

// Lots
export const LOTS_READ = "lots.read" as const
export const LOTS_WRITE = "lots.write" as const

// Parties
export const PARTIES_READ = "parties.read" as const
export const PARTIES_WRITE = "parties.write" as const

// Notifications -- per-user inbox; "write" covers mark-read /
// mark-dismissed, NOT inserting notifications (which is engine-only).
export const NOTIFICATIONS_READ = "notifications.read" as const
export const NOTIFICATIONS_WRITE = "notifications.write" as const

// Onboarding (admin — first-run wizard)
export const ONBOARDING_READ = "onboarding.read" as const
export const ONBOARDING_WRITE = "onboarding.write" as const

// Settings (admin)
export const SETTINGS_REGISTRY_READ = "settings.registry.read" as const
export const SETTINGS_REGISTRY_WRITE = "settings.registry.write" as const // M1 — Admin evolves the system without code
export const SETTINGS_INTEGRATIONS_READ = "settings.integrations.read" as const
export const SETTINGS_INTEGRATIONS_WRITE = "settings.integrations.write" as const
export const SETTINGS_ROLES_READ = "settings.roles.read" as const
export const SETTINGS_ROLES_WRITE = "settings.roles.write" as const

// Users (admin)
// `users.write` covers create + update + role-assignment (provisioning
// flow used by the first-run wizard stakeholders step + Settings → Users).
export const USERS_READ = "users.read" as const
export const USERS_WRITE = "users.write" as const

export const ALL_PERMISSIONS = [
	SHIPMENTS_READ,
	SHIPMENTS_WRITE,
	SHIPMENTS_EXPORT,
	DOCUMENTS_READ,
	DOCUMENTS_WRITE,
	DOCUMENTS_AUTOFLAG_CONFIGURE,
	LOTS_READ,
	LOTS_WRITE,
	ONBOARDING_READ,
	ONBOARDING_WRITE,
	PARTIES_READ,
	PARTIES_WRITE,
	NOTIFICATIONS_READ,
	NOTIFICATIONS_WRITE,
	SETTINGS_REGISTRY_READ,
	SETTINGS_REGISTRY_WRITE,
	SETTINGS_INTEGRATIONS_READ,
	SETTINGS_INTEGRATIONS_WRITE,
	SETTINGS_ROLES_READ,
	SETTINGS_ROLES_WRITE,
	USERS_READ,
	USERS_WRITE,
] as const

export type Permission = (typeof ALL_PERMISSIONS)[number]
